import { useEffect, useRef } from "react";
import { apiClientManager } from "@app/stores/api-manager";
import {
  useRealtimeStore,
  ConnectionStatus,
  ControlResult,
  ControlQueueItem,
} from "@/stores/realtimeStore";

// 로컬 타임아웃: 백엔드 노드 60초 TIMEOUT보다 약간 길게
const RECONCILE_TIMEOUT_MS = 65_000;
// pending 항목 주기적 스윕 간격
const SWEEP_INTERVAL_MS = 5_000;
// control-history가 아직 PUBLISHED(DB 마킹 지연)일 때 재시도
const PUBLISHED_RETRY_MS = 1_500;
const MAX_PUBLISHED_RETRIES = 2;
// SSE 재연결 후 버퍼 정리 여유
const RECONNECT_GRACE_MS = 2_000;

type HistoryStatus =
  | "PENDING"
  | "PUBLISHED"
  | "SUCCESS"
  | "FAILED"
  | "TIMEOUT";

interface UseControlReconciliationOptions {
  connectionStatus: ConnectionStatus;
  /** 재동기화로 확정된 결과를 SSE와 동일 경로로 라우팅 */
  onResolved: (result: ControlResult) => void;
}

/**
 * SSE 유실/재연결/30분 갭으로 control.result를 못 받아 pending에 고착되는 제어 명령을
 * control-history 조회로 재동기화한다. (Phase 1-2, 1-3)
 *
 * 트리거: ① 명령별 로컬 타임아웃(65초) ② SSE 재연결 직후.
 * V1 확정(2026-07-20 백엔드 소스 검증): 접수 응답의 commandId == control-history id.
 * 단일/배치 모두 fireControlCommand가 ControlCommandHistory.getId()를 commandId로 반환하며,
 * 이력 row는 접수 응답 전에 저장되므로 조회 404(NC005)는 매핑 오류가 아닌 이력 부재다.
 * 따라서 404는 상한 재시도 후 로컬 TIMEOUT 확정(무한 pending 방지),
 * 그 외 네트워크/5xx 일시 오류만 pending 유지(스윕이 재시도)한다.
 */
export function useControlReconciliation({
  connectionStatus,
  onResolved,
}: UseControlReconciliationOptions) {
  const onResolvedRef = useRef(onResolved);
  onResolvedRef.current = onResolved;

  // 재동기화 진행 중인 commandId (중복 호출 방지)
  const inFlightRef = useRef<Set<number>>(new Set());
  // PUBLISHED 재시도 횟수
  const retryCountRef = useRef<Map<number, number>>(new Map());
  const prevStatusRef = useRef<ConnectionStatus>(connectionStatus);

  useEffect(() => {
    const resolve = (item: ControlQueueItem, result: ControlResult) => {
      inFlightRef.current.delete(item.commandId);
      retryCountRef.current.delete(item.commandId);
      onResolvedRef.current(result);
    };

    const scheduleRetry = (item: ControlQueueItem) => {
      const retries = retryCountRef.current.get(item.commandId) ?? 0;
      if (retries >= MAX_PUBLISHED_RETRIES) {
        // 백엔드가 60초 내 확정하지 못함 → 로컬 TIMEOUT 확정
        retryCountRef.current.delete(item.commandId);
        resolve(item, buildResult(item, false, "응답 시간이 초과되었습니다."));
        return;
      }
      retryCountRef.current.set(item.commandId, retries + 1);
      inFlightRef.current.delete(item.commandId);
      window.setTimeout(() => reconcile(item), PUBLISHED_RETRY_MS);
    };

    const reconcile = async (item: ControlQueueItem) => {
      if (inFlightRef.current.has(item.commandId)) return;
      // 이미 SSE로 확정된 경우 skip
      const current = useRealtimeStore.getState().controlQueue.get(item.commandId);
      if (!current || current.status !== "pending") return;

      inFlightRef.current.add(item.commandId);
      try {
        const res = await apiClientManager.apis.node.getControlHistoryDetail({
          historyId: item.commandId,
        });
        const detail = res.data?.data;
        const status = detail?.status as HistoryStatus | undefined;

        switch (status) {
          case "SUCCESS":
            resolve(item, buildResult(item, true, undefined, detail?.completedAt));
            break;
          case "FAILED":
            resolve(
              item,
              buildResult(item, false, detail?.errorMessage ?? "제어에 실패했습니다.", detail?.completedAt, detail?.errorCode),
            );
            break;
          case "TIMEOUT":
            resolve(item, buildResult(item, false, "노드 응답 시간이 초과되었습니다.", detail?.completedAt));
            break;
          case "PENDING":
          case "PUBLISHED":
          default:
            // 아직 미확정(DB 마킹 지연 가능) → 잠시 후 재시도
            scheduleRetry(item);
            break;
        }
      } catch (error) {
        const status = (error as { response?: { status?: number } })?.response
          ?.status;
        if (status === 404) {
          // V1 확정으로 404는 이력 부재 확정 → PUBLISHED와 동일하게 상한 재시도 후
          // 로컬 TIMEOUT 확정해 무한 pending·무한 폴링을 방지한다.
          console.warn(
            `[ControlReconciliation] commandId=${item.commandId} 이력 없음(404) — 상한 재시도 후 TIMEOUT 확정`,
          );
          scheduleRetry(item);
          return;
        }
        // 네트워크/5xx 등 일시 오류 → pending 유지(스윕이 재시도)
        console.warn(
          `[ControlReconciliation] commandId=${item.commandId} 조회 실패 — pending 유지`,
          error,
        );
        inFlightRef.current.delete(item.commandId);
      }
    };

    // ── 트리거 ①: 주기적 스윕 (타임아웃 경과 pending) ──
    const timer = window.setInterval(() => {
      const now = Date.now();
      const queue = useRealtimeStore.getState().controlQueue;
      queue.forEach((item) => {
        if (item.status !== "pending") return;
        if (now - item.registeredAt < RECONCILE_TIMEOUT_MS) return;
        void reconcile(item);
      });
    }, SWEEP_INTERVAL_MS);

    // ── 트리거 ②: SSE 재연결 직후 pending 전체 재동기화 ──
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    const prev = prevStatusRef.current;
    if (connectionStatus === "connected" && prev !== "connected") {
      reconnectTimer = setTimeout(() => {
        const queue = useRealtimeStore.getState().controlQueue;
        queue.forEach((item) => {
          if (item.status === "pending") void reconcile(item);
        });
      }, RECONNECT_GRACE_MS);
    }
    prevStatusRef.current = connectionStatus;

    return () => {
      window.clearInterval(timer);
      if (reconnectTimer) clearTimeout(reconnectTimer);
    };
  }, [connectionStatus]);
}

function buildResult(
  item: ControlQueueItem,
  success: boolean,
  errorMessage?: string,
  timestamp?: string,
  errorCode?: string,
): ControlResult {
  return {
    commandId: item.commandId,
    nodeId: item.nodeId,
    nodeName: item.nodeName,
    controlType: item.controlType,
    success,
    data: null,
    errorCode: errorCode ?? null,
    errorMessage: errorMessage ?? null,
    timestamp,
  };
}
